import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Lock, Mail } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lux-workflow-test/artifacts/yi9ghwe1_Lux%20-%20Developments%20White%20and%20Orange%20With%20Grey%20Background.jpg";
const BG_IMAGE = "https://customer-assets.emergentagent.com/job_10acca96-4cc1-40c1-a886-8246fdce078d/artifacts/433twwvh_X%20Orange%20and%20White%20Washed%20Out%20With%20Grey%20Background.jpg";

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [previewHint, setPreviewHint] = useState(null);  // { email, password } in preview only
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Guards pass the page that needed a login (e.g. /diary from the Diary PWA).
  const returnTo = location.state?.from && location.state.from !== '/login' ? location.state.from : '/projects';

  // Fetch the public config (feature flag) — no auth required.
  // If the call fails for any reason, default to HIDING the Google button
  // (fail-closed). Password login is unaffected.
  //
  // URL PATTERN: Use the same relative-in-production / absolute-in-dev pattern
  // as the AuthContext `api` client (see AuthContext.jsx header). Using
  // REACT_APP_BACKEND_URL raw bakes the preview URL into the production
  // bundle and causes CORS failures on the live domain. See May 2026 fix.
  useEffect(() => {
    const apiBase = process.env.NODE_ENV === 'production'
      ? ''
      : (process.env.REACT_APP_BACKEND_URL || '');
    axios
      .get(`${apiBase}/api/config`)
      .then((res) => {
        setGoogleEnabled(Boolean(res.data?.google_auth_enabled));
        // preview_hint is ONLY rendered when BOTH:
        //   (a) the backend returned it (PREVIEW_HINTS_ENABLED=true in .env), AND
        //   (b) we're on a known preview hostname.
        // The hostname check is defense-in-depth: even if .env accidentally
        // ships to production (it has happened — backend/.env is tracked in git
        // despite the gitignore rule, since it pre-existed the rule), the hint
        // card will refuse to render on the live luxdev.app domain.
        const host = (typeof window !== 'undefined' ? window.location.hostname : '') || '';
        const isPreviewHost = host.endsWith('.preview.emergentagent.com')
                           || host.endsWith('.emergent.host')
                           || host === 'localhost'
                           || host === '127.0.0.1';
        if (
          isPreviewHost &&
          res.data?.preview_hint?.email &&
          res.data?.preview_hint?.password
        ) {
          setPreviewHint(res.data.preview_hint);
        }
      })
      .catch(() => setGoogleEnabled(false));
  }, []);

  const fillPreviewCredentials = () => {
    if (!previewHint) return;
    setEmail(previewHint.email);
    setPassword(previewHint.password);
  };

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH.
    const redirect = encodeURIComponent(`${window.location.origin}/auth/callback`);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate(returnTo, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image (hidden on mobile) */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#1E2530]/90 to-[#1E2530]/70" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            LUX DEVELOPMENTS
          </h2>
          <p className="text-lg text-gray-300 max-w-md">
            Operations Hub
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-[#1E2530]">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center lg:text-left">
            <img 
              src={LOGO_URL} 
              alt="LUX Developments" 
              className="h-32 w-auto mx-auto lg:mx-0 mb-6"
            />
            <h1 
              className="text-3xl font-bold text-white mb-2"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Sign In
            </h1>
            <p className="text-gray-400">
              Enter your credentials to access the operations portal
            </p>
          </div>

          <Card className="bg-[#2A3240] border-white/10">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@luxdev.co.uk"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="login-email-input"
                      className="pl-10 bg-[#1E2530] border-white/20 text-white placeholder:text-gray-500 focus:border-[#F5A623] focus:ring-[#F5A623]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-300">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="login-password-input"
                      className="pl-10 bg-[#1E2530] border-white/20 text-white placeholder:text-gray-500 focus:border-[#F5A623] focus:ring-[#F5A623]"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="w-full bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold py-5"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              {googleEnabled && (
                <>
                  <div className="my-6 flex items-center gap-3" data-testid="google-divider">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-xs uppercase tracking-wider text-gray-500">or</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>

                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    data-testid="google-signin-btn"
                    className="w-full flex items-center justify-center gap-3 bg-white text-[#1E2530] rounded-md py-3 font-medium hover:bg-gray-100 transition-colors"
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                      <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                    </svg>
                    Continue with Google
                  </button>
                </>
              )}

              {/* PREVIEW-ONLY hint card. Rendered only when the backend
                  /api/config returns preview_hint (PREVIEW_HINTS_ENABLED=true
                  in the gitignored preview .env). Production never sends
                  this payload, so this block stays dark on luxdev.app. */}
              {previewHint && (
                <div
                  className="mt-6 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-100"
                  data-testid="preview-hint-card"
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="font-semibold uppercase tracking-wider text-amber-300 text-[10px]">
                      Preview env — test credentials
                    </span>
                    <button
                      type="button"
                      onClick={fillPreviewCredentials}
                      className="text-amber-300 hover:text-amber-200 underline underline-offset-2"
                      data-testid="preview-hint-fill-btn"
                    >
                      Fill
                    </button>
                  </div>
                  <div className="space-y-0.5 font-mono">
                    <div><span className="text-amber-300/70">email:</span> {previewHint.email}</div>
                    <div><span className="text-amber-300/70">pass: </span> {previewHint.password}</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
